import { useState, useEffect, useRef, useContext, useCallback } from "react";
import moment from "moment-timezone";
import { useTimezone } from "../../hooks/useTimezone";
import { toast } from "react-toastify";
import { getTicket } from "../../services/tickets.api";
import { listWhatsapps } from "../../services/whatsapp.api";
import { listUsersSimple } from "../../services/users.api";
import { listContacts } from "../../services/contacts.api";
import { listSettings } from "../../services/settings.api";
import { findAllQueues } from "../../services/queue.api";
import { uploadMessageMedia } from "../../services/messages.api";
import {
  listScheduledMessagesByContact,
  createScheduledMessage,
  updateScheduledMessage,
  deleteScheduledMessage,
} from "../../services/scheduledIndividualMessages.api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getMinScheduledAt } from "./scheduledMessage.constants";

// D8.15.3 — Lógica (31 estados + effects + handlers + API) de ScheduledIndividualMessageModal.
export default function useScheduledIndividualMessage({ open, onClose, ticketId, editRecord = null }) {
  // Estas fechas son HORA DE PARED de la empresa: no se convierten. Con `moment()` a
  // secas se leian en la zona del NAVEGADOR. Ver doc 0412 §11.
  const { parseHoraDePared } = useTimezone();
  const { user } = useContext(AuthContext);

  // ── Contexto ticket ─────────────────────────────────────────────────────────
  const [contactId, setContactId]           = useState(null);
  const [contactName, setContactName]       = useState("");
  const [contactNumber, setContactNumber]   = useState("");
  const [ticketNumericId, setTicketNumericId] = useState(null);

  // ── Tabs ────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);

  // ── Formulario ──────────────────────────────────────────────────────────────
  const [body, setBody]                             = useState("");
  const [whatsappId, setWhatsappId]                 = useState("");
  const [selectedUserId, setSelectedUserId]         = useState("");
  const [scheduledAt, setScheduledAt]               = useState(getMinScheduledAt);
  const [recurrenceType, setRecurrenceType]         = useState("once");
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceEndAt, setRecurrenceEndAt]       = useState("");
  const [maxExecutions, setMaxExecutions]           = useState("");
  const [ticketAction, setTicketAction]             = useState("none");
  const [queueIdAction, setQueueIdAction]           = useState("");
  const [attachment, setAttachment]                 = useState(null);

  // ── Datos auxiliares ────────────────────────────────────────────────────────
  const [whatsappConnections, setWhatsappConnections] = useState([]);
  const [usersList, setUsersList]                     = useState([]);
  const [queues, setQueues]                           = useState([]);
  const [companyTimezone, setCompanyTimezone]         = useState("");

  // ── Loading ─────────────────────────────────────────────────────────────────
  const [loadingContext, setLoadingContext] = useState(false);
  const [submitting, setSubmitting]         = useState(false);

  // ── Historial ────────────────────────────────────────────────────────────────
  const [simHistory, setSimHistory]           = useState([]);
  const [loadingHistory, setLoadingHistory]   = useState(false);
  const [cancelTarget, setCancelTarget]       = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // ── Selector de contacto (solo cuando no hay ticketId) ──────────────────────
  const [selectedContact, setSelectedContact]   = useState(null);
  const [contactOptions, setContactOptions]     = useState([]);
  const [contactInputValue, setContactInputValue] = useState("");
  const [loadingContacts, setLoadingContacts]   = useState(false);

  // ── Menú de variables ────────────────────────────────────────────────────────
  const [varMenuAnchor, setVarMenuAnchor] = useState(null);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const fileInputRef = useRef(null);
  const bodyInputRef = useRef(null);
  const isMounted    = useRef(true);

  // ─────────────────────────────────────────────────────────────────────────────
  // Ciclo de vida
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoadingContext(true);

    (async () => {
      try {
        // Siempre cargan: departamentos, conexiones, usuarios, settings
        const sharedPromises = [
          findAllQueues(),
          listWhatsapps({ session: 0 }),
          listUsersSimple(),
          listSettings(),
        ];

        if (ticketId) {
          // Cuando viene de un ticket: cargamos también los datos del ticket
          const [ticketRes, queuesRes, whatsappRes, usersRes, settingsRes] =
            await Promise.all([getTicket(ticketId), ...sharedPromises]);
          if (!isMounted.current) return;

          const ticket = ticketRes.data;
          setContactId(ticket.contactId);
          setContactName(ticket.contact?.name || "");
          setContactNumber(ticket.contact?.number || "");
          setTicketNumericId(ticket.id);
          setWhatsappId(ticket.whatsappId || "");
          setSelectedUserId(user.id);
          setQueues(Array.isArray(queuesRes.data) ? queuesRes.data : []);
          setWhatsappConnections(Array.isArray(whatsappRes.data) ? whatsappRes.data : []);
          setUsersList(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users ?? []));

          const settings = Array.isArray(settingsRes.data) ? settingsRes.data : [];
          const tzSetting = settings.find(s => s.key === "timezone");
          if (tzSetting?.value) {
            const parts = tzSetting.value.split("/");
            setCompanyTimezone(parts[parts.length - 1].replace(/_/g, " "));
          }
        } else {
          // Cuando abre desde panel admin (sin ticket): carga todo menos el ticket
          const [queuesRes, whatsappRes, usersRes, settingsRes] =
            await Promise.all(sharedPromises);
          if (!isMounted.current) return;

          setSelectedUserId(user.id);
          setQueues(Array.isArray(queuesRes.data) ? queuesRes.data : []);
          setWhatsappConnections(Array.isArray(whatsappRes.data) ? whatsappRes.data : []);
          setUsersList(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users ?? []));

          const settings = Array.isArray(settingsRes.data) ? settingsRes.data : [];
          const tzSetting = settings.find(s => s.key === "timezone");
          if (tzSetting?.value) {
            const parts = tzSetting.value.split("/");
            setCompanyTimezone(parts[parts.length - 1].replace(/_/g, " "));
          }
        }
      } catch (err) {
        if (isMounted.current) toastError(err);
      } finally {
        if (isMounted.current) setLoadingContext(false);
      }
    })();
  }, [open, ticketId, user.id]);

  useEffect(() => {
    if (!open || !contactId) return;
    if (activeTab === 1) fetchHistory();
  }, [open, activeTab, contactId]);

  // Pre-carga campos cuando se abre en modo edición
  useEffect(() => {
    if (!open || !editRecord) return;
    setBody(editRecord.body || "");
    setWhatsappId(editRecord.whatsappId || "");
    setSelectedUserId(editRecord.userId || "");
    setScheduledAt(
      editRecord.scheduledAt
        ? (parseHoraDePared(editRecord.scheduledAt) || moment()).format("YYYY-MM-DDTHH:mm")
        : getMinScheduledAt()
    );
    setRecurrenceType(editRecord.recurrenceType || "once");
    setRecurrenceInterval(editRecord.recurrenceInterval || 1);
    setRecurrenceEndAt(
      editRecord.recurrenceEndAt
        ? moment(editRecord.recurrenceEndAt).format("YYYY-MM-DDTHH:mm")
        : ""
    );
    setMaxExecutions(editRecord.maxExecutions || "");
    setTicketAction(editRecord.ticketAction || "none");
    setQueueIdAction(editRecord.queueIdAction || "");
    // Datos del contacto del registro
    setContactId(editRecord.contactId || null);
    setContactName(editRecord.contact?.name || "");
    setContactNumber(editRecord.contact?.number || "");
    setTicketNumericId(editRecord.ticketId || null);
  }, [open, editRecord]);

  // Busca contactos en tiempo real (solo cuando el modal abre sin ticketId)
  useEffect(() => {
    if (ticketId || !open) return;
    const timer = setTimeout(async () => {
      setLoadingContacts(true);
      try {
        const { data } = await listContacts({ searchParam: contactInputValue, pageNumber: 1 });
        if (isMounted.current) {
          setContactOptions(Array.isArray(data.contacts) ? data.contacts : []);
        }
      } catch (_) {
        // silencioso — no interrumpir la UX
      } finally {
        if (isMounted.current) setLoadingContacts(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [contactInputValue, ticketId, open]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    if (!contactId) return;
    setLoadingHistory(true);
    try {
      const { data } = await listScheduledMessagesByContact(contactId);
      if (isMounted.current) setSimHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      if (isMounted.current) toastError(err);
    } finally {
      if (isMounted.current) setLoadingHistory(false);
    }
  }, [contactId]);

  const resetForm = () => {
    setBody("");
    setScheduledAt(getMinScheduledAt());
    setRecurrenceType("once");
    setRecurrenceInterval(1);
    setRecurrenceEndAt("");
    setMaxExecutions("");
    setTicketAction("none");
    setQueueIdAction("");
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    // Limpiar selector de contacto (solo aplica cuando se abre sin ticketId)
    if (!ticketId) {
      setSelectedContact(null);
      setContactInputValue("");
      setContactId(null);
      setContactName("");
      setContactNumber("");
    }
  };

  const handleClose = () => {
    resetForm();
    setActiveTab(0);
    onClose();
  };

  // Insertar variable en posición del cursor
  const handleInsertVar = varText => {
    setVarMenuAnchor(null);
    const input = bodyInputRef.current;
    if (!input) { setBody(p => p + varText); return; }
    const start = input.selectionStart ?? body.length;
    const end   = input.selectionEnd   ?? body.length;
    const next  = body.substring(0, start) + varText + body.substring(end);
    setBody(next);
    setTimeout(() => {
      input.setSelectionRange(start + varText.length, start + varText.length);
      input.focus();
    }, 0);
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) setAttachment(file);
  };

  const handleRemoveFile = () => {
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleTicketActionChange = e => {
    setTicketAction(e.target.value);
    if (e.target.value === "none") setQueueIdAction("");
  };

  // Submit
  const handleSubmit = async () => {
    if (!ticketId && !contactId) { toast.warning("Selecciona un contacto."); return; }
    if (!body.trim()) { toast.warning("El mensaje no puede estar vacío."); return; }
    if (!scheduledAt) { toast.warning("La fecha de envío es obligatoria."); return; }
    if (moment(scheduledAt).isBefore(moment().add(1, "minute"))) {
      toast.warning("La fecha de envío debe ser al menos 1 minuto en el futuro.");
      return;
    }
    if (recurrenceType === "custom" && (!recurrenceInterval || recurrenceInterval < 1)) {
      toast.warning("Para recurrencia personalizada el intervalo debe ser ≥ 1 día.");
      return;
    }
    if (!whatsappId) { toast.warning("Selecciona una conexión WhatsApp."); return; }

    setSubmitting(true);
    try {
      let mediaPath = null, mediaName = null, mediaType = null;
      if (attachment) {
        const fd = new FormData();
        fd.append("file", attachment);
        const { data: up } = await uploadMessageMedia(fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        mediaPath = up.path;
        mediaName = up.name || attachment.name;
        mediaType = attachment.type.split("/")[0];
      }

      const payload = {
        contactId,
        userId:             selectedUserId || user.id,
        whatsappId:         Number(whatsappId),
        ticketId:           ticketId || null,
        body:               body.trim(),
        mediaPath, mediaName,
        mediaType: mediaType ? (mediaType === "application" ? "document" : mediaType) : null,
        scheduledAt:        moment(scheduledAt).format("YYYY-MM-DD HH:mm:ss"),
        recurrenceType,
        recurrenceInterval: recurrenceType === "custom" ? Number(recurrenceInterval) : null,
        recurrenceEndAt:    recurrenceEndAt
          ? moment(recurrenceEndAt).format("YYYY-MM-DD HH:mm:ss")
          : null,
        maxExecutions: maxExecutions ? Number(maxExecutions) : null,
        ticketAction,
        queueIdAction: queueIdAction ? Number(queueIdAction) : null,
      };

      if (editRecord) {
        await updateScheduledMessage(editRecord.id, payload);
        toast.success("Recordatorio actualizado correctamente.");
      } else {
        await createScheduledMessage(payload);
        toast.success("Mensaje programado correctamente.");
      }
      resetForm();
      setActiveTab(1);
      fetchHistory();
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSubmitting(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await deleteScheduledMessage(cancelTarget.id);
      toast.success("Mensaje programado cancelado.");
      setCancelModalOpen(false);
      setCancelTarget(null);
      fetchHistory();
    } catch (err) {
      toastError(err);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Sub-renders
  // ─────────────────────────────────────────────────────────────────────────────

  return {
    activeTab,
    attachment,
    body,
    bodyInputRef,
    cancelModalOpen,
    cancelTarget,
    companyTimezone,
    contactId,
    contactInputValue,
    contactName,
    contactNumber,
    contactOptions,
    fetchHistory,
    fileInputRef,
    handleCancelConfirm,
    handleClose,
    handleFileChange,
    handleInsertVar,
    handleRemoveFile,
    handleSubmit,
    handleTicketActionChange,
    isMounted,
    loadingContacts,
    loadingContext,
    loadingHistory,
    maxExecutions,
    queueIdAction,
    queues,
    recurrenceEndAt,
    recurrenceInterval,
    recurrenceType,
    resetForm,
    scheduledAt,
    selectedContact,
    selectedUserId,
    setActiveTab,
    setAttachment,
    setBody,
    setCancelModalOpen,
    setCancelTarget,
    setCompanyTimezone,
    setContactId,
    setContactInputValue,
    setContactName,
    setContactNumber,
    setContactOptions,
    setLoadingContacts,
    setLoadingContext,
    setLoadingHistory,
    setMaxExecutions,
    setQueueIdAction,
    setQueues,
    setRecurrenceEndAt,
    setRecurrenceInterval,
    setRecurrenceType,
    setScheduledAt,
    setSelectedContact,
    setSelectedUserId,
    setSimHistory,
    setSubmitting,
    setTicketAction,
    setTicketNumericId,
    setUsersList,
    setVarMenuAnchor,
    setWhatsappConnections,
    setWhatsappId,
    simHistory,
    submitting,
    ticketAction,
    ticketNumericId,
    user,
    usersList,
    varMenuAnchor,
    whatsappConnections,
    whatsappId,
  };
}
